import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {isEnterKey, isSpaceKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export interface TabContainerHeaderDividerProps {
  className?: string;
}

export function TabContainerHeaderDivider(props: TabContainerHeaderDividerProps): JSX.Element {
  return <hr className={classNames('tabContainer-headerDivider', props.className)} />;
}

/** @package */
export interface NavItemProps<T extends string> {
  active: boolean;
  centerNavItem?: boolean;
  className?: string;
  onClick: (tabKey: T) => void;
  panelId: string;
  tabKey: T;
  title: ReactNode;
  tabId: string;
}

/** @package */
export function NavItem<T extends string>(props: NavItemProps<T>): JSX.Element {
  function handleClick(): void {
    props.onClick(props.tabKey);
  }

  return (
    <div
      aria-controls={props.panelId}
      aria-selected={props.active}
      className={classNames(
        'tabContainer-navItem',
        {
          [css(styles.centeredNavItem)]: props.centerNavItem,
          'tabContainer-navItem--active': props.active,
        },
        props.className
      )}
      id={props.tabId}
      onClick={handleClick}
      onKeyDown={(e: ElementKeyboardEvent) => {
        if (isSpaceKey(e) || isEnterKey(e)) {
          handleClick();
        }
      }}
      role="tab"
      tabIndex={props.active ? -1 : 0}
      {...getTestProps({component: 'NavItem', key: `${props.tabKey}`})}
    >
      {props.title}
    </div>
  );
}

export interface TabContainerProps<T extends string> {
  activeTab: T;
  centerNavItem?: boolean;
  children: ReactNode;
  className?: string;
  // Apply `u-flex-full-height`, which makes the tab container grow to fill its parent and allows children
  // to grow to fill the tab container.
  isFullHeight: boolean;
  headerDividerClassName?: string;
  onRequestTabChange: (tabKey: T) => void;
  navClassName?: string;
  paneClassName?: string;
  tabClassName?: string;
}

/*
 * TabContainer accepts tabKey on each Tab, and a defaultKey to specify which Tab to show first.
 * You can leave off tabKey and defaultKey to just show the first tab by default.
 */
export function TabContainer<T extends string>(props: TabContainerProps<T>): JSX.Element {
  function isActiveTab(tab: React.ReactElement<TabProps<T>>): boolean {
    return tab.props.tabKey === props.activeTab;
  }

  function handleSwitchTab(tabKey: T): void {
    props.onRequestTabChange(tabKey);
  }

  // Filter out null/undefined children
  const tabArray = React.Children.toArray(props.children).filter((child) => child) as Array<
    React.ReactElement<TabProps<T>>
  >;
  const [activeTab] = tabArray.filter((tab) => isActiveTab(tab));
  return (
    <div className={props.className} {...getTestProps({component: 'TabContainer', element: 'root'})}>
      <div className={classNames('tabContainer-nav', props.navClassName)} role="tablist">
        {tabArray.map((child) => (
          <NavItem
            key={child.props.tabKey}
            active={isActiveTab(child)}
            centerNavItem={props.centerNavItem}
            className={props.tabClassName}
            onClick={handleSwitchTab}
            panelId={`${child.props.tabKey}-panel`}
            tabId={`${child.props.tabKey}-tab`}
            tabKey={child.props.tabKey}
            title={child.props.title}
          />
        ))}
      </div>
      <TabContainerHeaderDivider className={props.headerDividerClassName} />
      <div
        aria-labelledby={`${activeTab.props.tabKey}-tab`}
        className={classNames('tabContainer-pane', props.paneClassName, {
          'u-flex-full-height': props.isFullHeight,
        })}
        id={`${activeTab.props.tabKey}-panel`}
        role="tabpanel"
        {...getTestProps({component: 'TabContainer', element: 'pane'})}
      >
        {activeTab.props.children}
      </div>
    </div>
  );
}

TabContainer.defaultProps = {
  isFullHeight: false,
};

export interface TabProps<T> {
  children: ReactNode;
  tabKey: T;
  title: ReactNode;
}

export function Tab<T>(_props: TabProps<T>): JSX.Element {
  throw new Error('Tab should not be actually rendered and is only used for external API');
}

const styles = StyleSheet.create({
  centeredNavItem: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    marginRight: 0,
  },
});
